// Too much files to change
/* eslint-disable import/no-internal-modules */
import { Component, ComponentChild } from 'preact'
import Panel from '../../../../control-components/panel'
import InputGroup from '../../../../control-components/ui-components/InputGroup'
import FeaturedTopicsManager from './FeaturedTopicsManager'
import axios, { AxiosResponse } from 'axios'
import { isInputEl } from '../../../../libs/typeGuards'

export interface FeaturedTopicInterface {
  id: number
  title: string
  url: string
  contentType: string
  type?: string
}

interface Content {
  topics: Array<number | TopicData>
  firstTopicsNumber?: number
  buttonLabel?: string
  buttonURL?: string
}

interface TopicData {
  id: number
  type: string
}

interface NewData {
  topics: TopicData[]
  firstTopicsNumber: number
  buttonLabel: string
  buttonURL: string
}

interface Props {
  onChange: (state: NewData) => void
  switcher?: ComponentChild
  dragHandle?: ComponentChild
  content: Content
}

interface State {
  featuredTopics: FeaturedTopicInterface[]
  firstTopicsNumber: number
  buttonLabel: string
  buttonURL: string
  originalTopics: FeaturedTopicInterface[]
}

export class FeaturedTopicsConfigurator extends Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.state = {
      firstTopicsNumber: props.content.firstTopicsNumber || 0,
      buttonLabel: props.content.buttonLabel || '',
      buttonURL: props.content.buttonURL || '',
      featuredTopics: [],
      originalTopics: []
    }

    this.setValue = this.setValue.bind(this)
    this.onChangeFirstTopicsNumber = this.onChangeFirstTopicsNumber.bind(this)
    this.onChangeButtonLabel = this.onChangeButtonLabel.bind(this)
    this.onChangeButtonURL = this.onChangeButtonURL.bind(this)
    this.onChangeSend = this.onChangeSend.bind(this)
  }

  async componentDidMount() {
    const { data }: AxiosResponse<{ content: FeaturedTopicInterface[] }> = await axios.get(
      '/homepage/featuredTopics',
      {
        params: {
          topicIds: this.props.content.topics
        }
      }
    )

    this.setState({
      featuredTopics: data.content
    })
  }

  onChangeFirstTopicsNumber(newFirstTopicsNumber: number) {
    this.setState(
      {
        firstTopicsNumber: newFirstTopicsNumber
      },
      () => this.onChangeSend()
    )
  }

  onChangeButtonLabel(newButtonLabel: string) {
    this.setState(
      {
        buttonLabel: newButtonLabel
      },
      () => this.onChangeSend()
    )
  }

  onChangeButtonURL(newButtonURL: string) {
    this.setState(
      {
        buttonURL: newButtonURL
      },
      () => this.onChangeSend()
    )
  }

  setValue(_: string, value: FeaturedTopicInterface[]) {
    this.setState(
      {
        featuredTopics: value
      },
      () => this.onChangeSend()
    )
  }

  onChangeSend() {
    const newData: NewData = {
      firstTopicsNumber: this.state.firstTopicsNumber,
      topics: this.state.featuredTopics?.map((t) => ({ id: t.id, type: t.contentType })),
      buttonLabel: this.state.buttonLabel,
      buttonURL: this.state.buttonURL
    }

    this.props.onChange(newData)
  }

  render() {
    return (
      <div>
        <div className='featured-topics__first-topics-width'>
          <p className='featured-topics__first-topics-width-text'>
            Display first
            <input
              type='number'
              onChange={(e) => {
                if (!isInputEl(e.target)) return
                this.onChangeFirstTopicsNumber(parseInt(e.target.value, 10))
              }}
              value={this.state.firstTopicsNumber}
              min={0}
            />
            topics full-width on desktop
          </p>
        </div>
        <FeaturedTopicsManager
          searchPlaceholder='Add a topic by pasting topic URL or searching'
          onUpdate={(key, value) => this.setValue(key, value)}
          featuredTopics={this.state.featuredTopics}
          title='Topics'
        />
        <div className='featured-topics__buttons'>
          <InputGroup
            title='Button label (optional)'
            type='text'
            placeholder='Show more'
            onChange={(e: { target: HTMLInputElement }) => {
              this.onChangeButtonLabel(e.target.value)
            }}
            value={this.state.buttonLabel}
          />
          <InputGroup
            title='Button URL (optional)'
            type='url'
            placeholder='https://example.com'
            onChange={(e: { target: HTMLInputElement }) => {
              this.onChangeButtonURL(e.target.value)
            }}
            value={this.state.buttonURL}
          />
        </div>
      </div>
    )
  }
}

interface PanelProps extends Props {
  dragHandle: JSX.Element
  switcher: JSX.Element
}

const FeaturedTopicsPanel = (props: PanelProps) => (
  <Panel
    collapsible
    customClass='homepage-widget featured-topics-widget'
    title={
      <div className='panel-title-with-switch'>
        <div className='homepage-reorderable-panel-title'>
          {props.dragHandle}
          <span>Featured Topics</span>
        </div>
        {props.switcher}
      </div>
    }
  >
    <FeaturedTopicsConfigurator {...props} />
  </Panel>
)

export default FeaturedTopicsPanel
